/**
 * Axios error postprocessing
 *
 * @package Muval Frontend SPA
 * @author Tim Maier
 */
import { toast } from 'vue-sonner';
import * as Sentry from '@sentry/vue';
import { useClipboard } from '@vueuse/core';

const { copy } = useClipboard({
    legacy: true,
});

/**
 * Client-side transport failures that are not actionable app bugs.
 * ERR_NETWORK covers XHR status 0 (offline, Wi‑Fi drop, sleep/wake); ERR_CANCELED is intentional abort.
 */
function isNoiseAxiosError(error) {
    return error?.code === 'ERR_CANCELED' || error?.code === 'ERR_NETWORK' || error?.message === 'Network Error';
}

/**
 * A 402 means the request was fine and the bank said no. A declined card is a
 * routine operator outcome, not an app fault, so it must not become a Sentry
 * event — at the rate cards decline it would bury genuine errors.
 */
function isDeclinedPayment(error) {
    return error?.response?.status === 402;
}

/**
 * Helper function to process Axios errors
 *
 * @param error Axios error object
 * @return object Error Muval SFC can use
 */
export function processError(error) {
    if (
        import.meta.env.VITE_ENV == 'production' &&
        !isNoiseAxiosError(error) &&
        !isDeclinedPayment(error) &&
        error instanceof Error
    ) {
        Sentry.captureException(error);
    }

    let result = {
        code: null,
        message: 'Unknown error',
        errors: null,
        links: null,
        data: null,
    };

    if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx

        switch (error.response.status) {
            case 401:
                result.message = error?.response?.data?.message;
                break;

            case 422:
                // toast.error(error?.response?.data?.message ?? 'An error has occurred.'); // Disabled the toast notif for 422 errors.
                result.message = error.response.data.message;
                result.errors = error.response.data.errors;
                result.links = error.response.data.links;
                break;

            default:
                if (error.stack) {
                    delete error.stack;
                }

                if (error?.config?.xSilent === true) {
                    console.log(
                        'Skipped toast as request was set to silent:',
                        error?.response?.data?.message ?? 'An error has occurred.',
                    );
                } else if (!useUserStore().isAuthenticated) {
                    console.log(
                        'Skipped toast as user is not authenticated:',
                        error?.response?.data?.message ?? 'An error has occurred.',
                    );
                } else {
                    toast.error(error?.response?.data?.message ?? 'An error has occurred.', {
                        action: {
                            label: 'Copy details',
                            onClick: () => {
                                const userStore = useUserStore();
                                copy(JSON.stringify({ error, user: userStore.data }));
                            },
                        },
                    });
                }

                result.message = error?.response?.data?.message;
                result.errors = error?.response?.data?.errors;

                break;
        }

        result.code = error.response.status;
        result.data = error.response.data;
    } else if (error.request) {
        // The request was made but no response was received
        if (error.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
            return;
        }

        if (error.stack) {
            delete error.stack;
        }

        result.message = error?.message ?? 'Network Error';
        result.code = error?.code ?? 'ERR_NETWORK';

        if (error?.config?.xSilent === true) {
            console.log('Skipped toast as request was set to silent:', result.message);
        } else if (!useUserStore().isAuthenticated) {
            console.log('Skipped toast as user is not authenticated:', result.message);
        } else if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            console.log('Skipped toast as browser is offline:', result.message);
        } else {
            toast.error('An error has occurred.', {
                action: {
                    label: 'Copy details',
                    onClick: () => {
                        const userStore = useUserStore();
                        copy(JSON.stringify({ error: error, user: userStore.data }));
                    },
                },
            });
        }
    } else {
        // Something happened in setting up the request that triggered an Error
        result.message = 'Axios error: ' + error.message;
    }

    return result;
}
